import React, { useState, useEffect, useCallback } from "react";
import { Input, Table, Select, Button } from "antd";
import Menubar from "../../../components/WhrManager/App";
import { http } from "../../../services/http";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Navbar from "../../../components/navbar";
import { useSelector } from "react-redux";
import ViewMaterialReports from "../../../components/ViewMaterialReports/index";
import { debouncedSet } from "../../../utils/debounce";

const MaterialReports = () => {
  const { t } = useTranslation();
  const { warehouse } = useSelector((state) => state.main) || "";

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [itemName, setItemName] = useState("");

  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [whsCode, setWhsCode] = useState(warehouse);
  const [itemGroup, setItemGroup] = useState("");
  const [typeGroup, setTypeGroup] = useState("");
  const [subGroup, setSubGroup] = useState("");
  const [isOnHand, setIsOnHand] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [pageSize] = useState(30);

  const [whsData, setWhsData] = useState([]);
  const [subGroupData, setSubGroupData] = useState([]);
  const [typeGroupData, setTypeGroupData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [itmGrpData, setItmGrpData] = useState([]);

  const handleNextPage = () => {
    if (hasMoreData) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const fetchData = useCallback(
    async (startDate, endDate) => {
      try {
        setLoading(true);

        let url = `api/dashboard/material-report?itemName=${itemName}&startDate=${startDate}&endDate=${endDate}&whsCode=${whsCode}&itemGroup=${itemGroup}&typeGroup=${typeGroup}&subGroup=${subGroup}&isOnHand=${isOnHand}&skip=${currentPage}&pageSize=${pageSize}`;

        const { data } = await http.get(url);

        setHasMoreData(data.length === pageSize);

        setDataSource(data);
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    },
    [
      itemName,
      startDate,
      endDate,
      whsCode,
      itemGroup,
      typeGroup,
      subGroup,
      isOnHand,
      currentPage,
    ],
  );

  const fetchWarehouse = useCallback(async () => {
    try {
      const { data } = await http.get(`api/warehouses`);

      setWhsData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchItemGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/item-groups`);

      setItmGrpData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchSubGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/user-defined-fields`, {
        params: {
          FieldId: 1,
          TableID: "OITM",
        },
      });

      setSubGroupData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchTypeGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/user-defined-fields`, {
        params: {
          FieldId: 0,
          TableID: "OITM",
        },
      });

      setTypeGroupData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    fetchData(startDate, endDate);
  }, [fetchData, startDate, endDate]);

  useEffect(() => {
    fetchWarehouse();
    fetchSubGroups();
    fetchTypeGroups();
    fetchItemGroups();
  }, [fetchWarehouse, fetchSubGroups, fetchTypeGroups, fetchItemGroups]);

  const handleStartDate = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDate = (e) => {
    setEndDate(e.target.value);
  };

  const columns = [
    {
      title: t("itemName"),
      dataIndex: "itemName",
      key: "itemName",
      width: 350,
    },
    {
      title: t("itemCode"),
      dataIndex: "itemCode",
      key: "itemCode",
    },
    {
      title: t("invntryUom"),
      dataIndex: "invntryUom",
      key: "invntryUom",
    },
    {
      title: t("itmsGrpNam"),
      dataIndex: "itmsGrpNam",
      key: "itmsGrpNam",
    },
    {
      title: t("u_typegroup"),
      dataIndex: "u_typegroup",
      key: "u_typegroup",
    },
    {
      title: t("u_subgroup"),
      dataIndex: "u_subgroup",
      key: "u_subgroup",
    },

    {
      title: t("balanceBeginningPeriod"),
      dataIndex: "balanceBeginningPeriod",
      key: "balanceBeginningPeriod",
      render: (text) => {
        return new Intl.NumberFormat("fr-FR").format(text) || "-";
      },
    },
    {
      title: t("income"),
      dataIndex: "income",
      key: "income",
      render: (text) => {
        return new Intl.NumberFormat("fr-FR").format(text) || "-";
      },
    },
    {
      title: t("outcome"),
      dataIndex: "outcome",
      key: "outcome",
      render: (text) => {
        return new Intl.NumberFormat("fr-FR").format(text) || "-";
      },
    },

    {
      title: t("balanceFinishingPeriod"),
      dataIndex: "balanceFinishingPeriod",
      key: "balanceFinishingPeriod",
      render: (text) => {
        return new Intl.NumberFormat("fr-FR").format(text) || "-";
      },
    },
  ];

  const handleFilterChange = (value, setState) => {
    setCurrentPage(0);
    setState(value);
  };

  const handleExportExcel = async () => {
    setExcelLoading(true);
    let backUrl = `api/dashboard/material-report/excel`;
    const body = {
      startDate,
      endDate,
      whsCode,
      itemGroup,
      typeGroup,
      subGroup,
      isOnHand,
      skip: 0,
      pageSize: 10000,
      itemName,
    };

    try {
      const res = await http.post(backUrl, body, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob", // Ensure the response is treated as a blob
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Materiallar hisoboti.xlsx"); // Define the download file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {}
    setExcelLoading(false);
  };

  const handleFilterChangeDebounced = (value, setState) => {
    setCurrentPage(0);
    debouncedSet(value, setState);
  };
  return (
    <div className="flex w-full">
      <Menubar />
      <div className="h-screen w-full overflow-y-auto">
        <div className="h-screen w-full overflow-y-auto">
          <Navbar />
          <h1 className="font-poppins ml-4 mt-10 text-xl font-bold text-black sm:ml-5 sm:mt-14 sm:text-2xl">
            {t("materialReports")}
          </h1>
          <div className="mt-10 w-full border-[1px] border-[#E8E8E8] sm:mt-14"></div>

          <div className="mt-6 flex w-full flex-wrap items-start gap-4 px-4 sm:mt-10 sm:gap-7 sm:px-10">
            <div className="flex flex-col gap-3">
              <p>{t("startDate")}</p>
              <Input
                type="date"
                value={startDate}
                className="w-[150px]"
                onChange={handleStartDate}
                max={endDate}
              />
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("endDate")}</p>
              <Input
                type="date"
                value={endDate}
                className="w-[150px]"
                onChange={handleEndDate}
                min={startDate}
                max={moment().format("YYYY-MM-DD")}
              />
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("whs")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={whsCode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setWhsCode)
                }
              >
                {whsData.map((val) => (
                  <Select.Option key={val} value={val.warehouseCode}>
                    {val.warehouseName}
                  </Select.Option>
                ))}
              </Select>
              {!whsCode && (
                <p className="text-red-500">{t("pleaseSelectWarehouse")}</p>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("itemName")}</p>
              <Input
                placeholder={t("itemName")}
                allowClear
                onChange={(e) =>
                  handleFilterChangeDebounced(e.target.value || "", setItemName)
                }
              />
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("itmsGrpNam")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={itemGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setItemGroup)
                }
              >
                {itmGrpData.map((val) => (
                  <Select.Option key={val} value={val.itmsGrpCod}>
                    {val.itmsGrpNam}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("u_typegroup")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={typeGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setTypeGroup)
                }
              >
                {typeGroupData.map((val) => (
                  <Select.Option key={val} value={val.fldValue}>
                    {val.descr}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("u_subgroup")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={subGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setSubGroup)
                }
              >
                {subGroupData.map((val) => (
                  <Select.Option key={val} value={val.fldValue}>
                    {val.descr}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("isOnHand")}</p>
              <Select
                allowClear
                style={{ width: 150 }}
                value={isOnHand}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setIsOnHand)
                }
              >
                <Select.Option value={""}>Barchasi</Select.Option>
                <Select.Option value={"Y"}>Mavjud</Select.Option>
                <Select.Option value={"N"}>Mavjud emas</Select.Option>
              </Select>
            </div>
          </div>

          <div className="mb-4 flex flex-col justify-between px-4 sm:mt-10 sm:flex-row sm:gap-7">
            <div className="font-nunitto font-bold">
              {t("page")} : {currentPage + 1}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className="h-[40px] w-[120px] rounded-2xl bg-gray-300 text-gray-700 disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("previous")}
              </button>
              <button
                onClick={handleNextPage}
                disabled={!hasMoreData}
                className="h-[40px] w-[120px] rounded-2xl bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("next")}
              </button>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end px-4">
            <Button
              onClick={handleExportExcel}
              loading={excelLoading}
              type="primary"
              className="ml-5 h-10 w-full rounded-lg bg-green-700 text-white sm:w-[200px]"
            >
              {t("exportExcel")}
            </Button>
          </div>
          <div className="mb-4 px-4 sm:mt-10 sm:flex-row sm:gap-7 sm:px-4">
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              className="mt-6 w-full sm:mt-10"
              rowKey="zakaz"
              sticky={true}
              scroll={{ x: 1700 }}
              loading={loading}
              onRow={(record) => {
                return {
                  onClick: () => {
                    setSelectedRow(record);
                    setIsModalVisible(true);
                  },
                };
              }}
            />
          </div>
        </div>
        <ViewMaterialReports
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
          }}
          data={selectedRow}
          filters={{
            startDate,
            endDate,
          }}
        />
      </div>
    </div>
  );
};

export default MaterialReports;
